import jsPDF from 'jspdf';
import autoTable, { __createTable, __drawTable, type CellInput, type RowInput, type UserOptions } from 'jspdf-autotable';
import {
    PDFBool,
    PDFDocument,
    PDFHexString,
    PDFName,
    PDFTextField,
    StandardFonts,
    rgb,
    type PDFField,
    type PDFFont,
} from 'pdf-lib';
import { buildCompanyForPdf } from '../../services/pdf/companyForPdf';
import {
    A4_GEOMETRY,
    createLetterhead,
    drawFooters,
    drawPage1Header,
    type CompanyInfo,
} from '../../services/pdf/letterhead';
import type { Anschluss, PdfKontext } from './planungIntegration';
import {
    FELD_AUSGANG,
    FELD_META,
    HANDBUCH_GRENZEN,
    istHandbuchFeld,
    pruefeFeldanzahl,
    pruefeFeldwert,
    pruefeHandbuchDatei,
    werteHandbuchFelderAus,
    type HandbuchBlock,
    type HandbuchGelesen,
    type HandbuchModell,
    type RohFeld,
} from './unterlagenHandbuch';

/**
 * Das Handbuch der Unterlagen-Checkliste als ausfuellbares PDF — und das
 * Einlesen der ausgefuellten Notizen.
 *
 * ═══ AUFBAU ══════════════════════════════════════════════════════════════
 * Wie eine Bauunterlage, nicht wie ein Bildschirmausdruck — und aus einem
 * Guss mit der Klaerungsliste (`generatorPlanungsfragen.ts`):
 *   1. Protokollkopf als Rastertabelle (Bauvorhaben, Projekt-Nr., Bauherr,
 *      Stand, Aufgestellt, Ansicht) und eine Zaehlzeile,
 *   2. Legende und Vorbemerkung als gerahmter Block (Kuerzel, Fussnote,
 *      Art-C-Grundsatz als „Allgemeiner Hinweis"),
 *   3. je Gruppe eine Tabelle mit Positionsnummern; die letzte Spalte ist
 *      das ausfuellbare Notizfeld.
 * Schwarz auf weiss, duenne graue Rasterlinien, Kopfzeilen hellgrau — keine
 * Farbtexte, keine Etiketten.
 *
 * ═══ WARUM jsPDF UND pdf-lib ═════════════════════════════════════════════
 * Briefkopf, Briefpapier und Fusszeile stehen in `letterhead.ts`, und das
 * arbeitet mit jsPDF; einen zweiten Briefkopf in pdf-lib nachzubauen hiesse,
 * ihn zweimal zu pflegen. jsPDF kann zwar Formularfelder, erzeugt aber keine
 * brauchbaren Erscheinungsbilder fuer Umlaute in mehrzeiligen Feldern;
 * pdf-lib kann das und liest Feldwerte zuverlaessig zurueck. Deshalb:
 *   1. jsPDF + autoTable setzen das Blatt; die Zellen der Notizspalte werden
 *      beim Zeichnen (`didDrawCell`) samt Seite gemerkt,
 *   2. pdf-lib laedt das Ergebnis und legt die Textfelder genau dorthin —
 *      den Rahmen zeichnet die Tabelle.
 *
 * ═══ UMLAUTE UND SONDERZEICHEN ═══════════════════════════════════════════
 * Der Feldwert wird als UTF-16 gespeichert — er nimmt jedes Zeichen auf und
 * kommt beim Einlesen unveraendert zurueck. Das ERSCHEINUNGSBILD setzt
 * Helvetica mit WinAnsi: ä ö ü ß „ " – € sind dabei, ein Zeichen ausserhalb
 * davon wird NUR im Bild durch „?" ersetzt, nie im Wert. `NeedAppearances`
 * bittet die Programme zusaetzlich, das Bild aus dem Wert neu zu zeichnen.
 */

const MM = 72 / 25.4;
/** Millimeter je Punkt. */
const PT = 25.4 / 72;
const ZEILENFAKTOR = 1.15;
const zeilenhoehe = (pt: number) => pt * PT * ZEILENFAKTOR;

const SCHRIFT = {
    titel: 13,
    untertitel: 9.5,
    tabelle: 8,
    tabellenkopf: 7.5,
    gruppe: 8.5,
    name: 8.5,
    erlaeuterung: 7,
    legende: 7,
    fusszeile: 7,
    /** Schrift im Notizfeld. */
    feld: 8,
};

type Farbe = [number, number, number];
const FARBE: Record<'text' | 'grau' | 'linie' | 'kopf' | 'gruppe', Farbe> = {
    text: [17, 24, 39],
    grau: [75, 85, 99],
    linie: [190, 195, 202],
    kopf: [243, 244, 246],
    gruppe: [226, 228, 232],
};

/** Innenabstand der Tabellenzellen in mm. */
const POLSTER = 1.6;
/** Mindesthoehe einer Zeile = Notizfeld mit rund drei sichtbaren Zeilen. */
export const NOTIZ_HOEHE = 14;
/** Oberkante der Tabellen auf Folgeseiten ohne Briefpapier — unter dem
 *  Firmennamen, den `drawFooters` dort bei 12 mm setzt. */
const FOLGESEITE_OBEN = 18;
/** Hoechstlaenge des Namens im Blatt; laengere Namen (nur eigene oder aus
 *  dem SiGe-Plan moeglich) werden gekuerzt. */
export const NAME_ZEICHEN = 300;
/** Hinweis in der Zelle, wenn Name oder Erlaeuterung gekuerzt wurden. */
export const GEKUERZT = 'Gekürzt; der vollständige Text steht in der App.';

/** Spaltenbreiten in mm; „Unterlage / Erlaeuterung" bekommt den Rest der
 *  Inhaltsbreite, damit die Tabelle exakt die Breite des Briefpapiers hat. */
const SPALTEN = { pos: 11, art: 8, lief: 13, status: 14, notizen: 52 };
export const spaltenbreiten = (inhaltsbreite: number) => ({
    ...SPALTEN,
    unterlage: inhaltsbreite - (SPALTEN.pos + SPALTEN.art + SPALTEN.lief + SPALTEN.status + SPALTEN.notizen),
});

/** Wo ein Notizfeld auf dem Blatt liegt (mm, y von oben). */
export interface FeldRechteck {
    feld: string;
    seite: number;
    x: number;
    yOben: number;
    breite: number;
    hoehe: number;
    text: string;
}

/** Seite, auf der jsPDF gerade zeichnet. */
const aktuelleSeite = (doc: jsPDF): number =>
    (doc.internal as unknown as { getCurrentPageInfo: () => { pageNumber: number } }).getCurrentPageInfo().pageNumber;

/** Grundstil aller Tabellen: Raster duenn und grau, Text fast schwarz. */
const grundstil = {
    font: 'helvetica' as const,
    fontSize: SCHRIFT.tabelle,
    textColor: FARBE.text,
    lineColor: FARBE.linie,
    lineWidth: 0.2,
    cellPadding: POLSTER,
    valign: 'top' as const,
    overflow: 'linebreak' as const,
};
const kopfstil = {
    fillColor: FARBE.kopf,
    textColor: FARBE.text,
    fontStyle: 'bold' as const,
    fontSize: SCHRIFT.tabellenkopf,
    lineColor: FARBE.linie,
    lineWidth: 0.2,
};

/**
 * Setzt das Blatt mit jsPDF und autoTable und gibt die Rechtecke der
 * Notizfelder zurueck. Exportiert fuer Tests: das Layout laesst sich so
 * pruefen, ohne pdf-lib.
 */
export const zeichneHandbuch = (doc: jsPDF, modell: HandbuchModell, company: CompanyInfo): FeldRechteck[] => {
    const geo = A4_GEOMETRY;
    const { margin, contentWidth, pageHeight } = geo;
    const felder: FeldRechteck[] = [];

    const lh = createLetterhead(doc, company, geo);
    lh.stampCurrentBg();
    const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, geo);
    const rand = { ...lh.autoTableMargin(), top: lh.hasStationery ? lh.topFollow : FOLGESEITE_OBEN };
    const unterkante = pageHeight - rand.bottom;
    const farbe = (f: Farbe) => doc.setTextColor(f[0], f[1], f[2]);
    const endeVon = (t: { finalY?: number } | undefined, sonst: number) =>
        (t && typeof t.finalY === 'number' ? t.finalY : sonst);
    const letzteTabelle = () => (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable;

    // ── Titel ────────────────────────────────────────────────────────────
    let y = Math.max(addressFieldY, rightColBottom + 8) + 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.titel);
    farbe(FARBE.text);
    doc.text(modell.titel, margin, y);
    y += 5.2;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    farbe(FARBE.grau);
    doc.text(modell.untertitel, margin, y);
    y += 4;

    // ── Protokollkopf ────────────────────────────────────────────────────
    const k = modell.kopf;
    const aufgestellt = [company && company.name ? company.name : '', k.aufgestelltVon].filter(Boolean).join(', ');
    const breit = (inhalt: string): CellInput => ({ content: inhalt, colSpan: 3 });
    autoTable(doc, {
        startY: y,
        margin: rand,
        theme: 'grid',
        willDrawPage: lh.willDrawPage,
        styles: { ...grundstil, cellPadding: 1.4 },
        columnStyles: {
            0: { cellWidth: 26, fontStyle: 'bold', fillColor: FARBE.kopf },
            1: { cellWidth: contentWidth / 2 - 26 },
            2: { cellWidth: 26, fontStyle: 'bold', fillColor: FARBE.kopf },
            3: { cellWidth: contentWidth / 2 - 26 },
        },
        body: [
            ['Bauvorhaben', breit(k.bauvorhaben)],
            ['Projekt-Nr.', k.projektNummer, 'Bauherr', k.bauherr],
            ['Stand', k.stand, 'Aufgestellt', aufgestellt],
            ['Ansicht', breit(k.ansicht)],
        ],
    });
    y = endeVon(letzteTabelle(), y) + 3;

    // ── Zaehlzeile ───────────────────────────────────────────────────────
    const z = modell.zaehlung;
    autoTable(doc, {
        startY: y,
        margin: rand,
        theme: 'grid',
        willDrawPage: lh.willDrawPage,
        styles: { ...grundstil, halign: 'center', cellPadding: 1.2 },
        headStyles: { ...kopfstil, halign: 'center' },
        head: [['Relevant', 'Liegt vor (V)', 'Angefordert (A)', 'Offen (O)', 'Entfällt (E)', 'Erforderlich, nicht angefordert']],
        body: [[z.relevant, z.liegtVor, z.angefordert, z.offen, z.entfaellt, z.erforderlichNichtAngefordert].map(String)],
        columnStyles: Object.fromEntries([0, 1, 2, 3, 4, 5].map(i => [i, { cellWidth: contentWidth / 6 }])),
    });
    y = endeVon(letzteTabelle(), y) + 3;

    // ── Legende und Vorbemerkung ─────────────────────────────────────────
    autoTable(doc, {
        startY: y,
        margin: rand,
        theme: 'plain',
        willDrawPage: lh.willDrawPage,
        tableLineColor: FARBE.linie,
        tableLineWidth: 0.2,
        styles: { ...grundstil, fontSize: SCHRIFT.legende, cellPadding: { top: 0.7, bottom: 0.7, left: POLSTER, right: POLSTER } },
        headStyles: { ...kopfstil, cellPadding: { top: 1.2, bottom: 1.2, left: POLSTER, right: POLSTER } },
        head: [[{ content: 'Legende und Vorbemerkung', colSpan: 2 }]],
        body: modell.legende.map(l => [l.label, l.text]),
        columnStyles: {
            0: { cellWidth: 30, fontStyle: 'bold' },
            1: { cellWidth: contentWidth - 30 },
        },
    });
    y = endeVon(letzteTabelle(), y) + 6;

    // ── Positionen je Gruppe ─────────────────────────────────────────────
    const sp = spaltenbreiten(contentWidth);
    const textBreite = sp.unterlage - 2 * POLSTER;

    /** Umbruch je Position einmal — fuer die Hoehe UND fuer das Zeichnen. */
    // Eine Position darf hoechstens 60 % der nutzbaren Seitenhoehe einnehmen.
    // Hoeher hilft `rowPageBreak: 'avoid'` nicht mehr — autoTable risse die
    // Zeile, und der eigene Zeichen-Callback schriebe ueber Rahmen und
    // Seitenrand. Laengeres wird gekuerzt (das Original bleibt in der App).
    const maxPositionHoehe = 0.6 * (unterkante - rand.top);
    const satz = new Map<string, { name: string[]; erlaeuterung: string[]; hoehe: number }>();
    const satzVon = (b: HandbuchBlock) => {
        const vorhanden = satz.get(b.id);
        if (vorhanden) return vorhanden;
        const zhName = zeilenhoehe(SCHRIFT.name);
        const zhErl = zeilenhoehe(SCHRIFT.erlaeuterung);
        /** Hoehe fuer Text in der Zelle (ohne Polster). */
        const innen = maxPositionHoehe - 2 * POLSTER;
        const hoeheVon = (nameZeilen: number, erlZeilen: number) =>
            nameZeilen * zhName + (erlZeilen ? 0.8 + erlZeilen * zhErl : 0);
        /** Letzte Zeile mit „…" — ein paar Zeichen Luft, damit sie nicht ueber die Zelle laeuft. */
        const mitAuslassung = (zeile: string, zusatz = '') => `${zeile.replace(/\s*\S{0,5}$/, '')} …${zusatz}`;

        // Name: Umbrueche und Tabs werden Leerzeichen (ein Name mit 200
        // Zeilenumbruechen saehe sonst wie 200 Zeilen aus), dann auf
        // NAME_ZEICHEN begrenzt.
        const nameFlach = b.name.replace(/\s+/g, ' ').trim();
        const nameGekuerzt = nameFlach.length > NAME_ZEICHEN;
        const nameText = nameGekuerzt ? `${nameFlach.slice(0, NAME_ZEICHEN).trimEnd()} …` : nameFlach;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.name);
        let name = doc.splitTextToSize(`${nameText}${b.fachpraxis ? ' *' : ''}`, textBreite) as string[];

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.erlaeuterung);
        let erlaeuterung = b.erlaeuterung.flatMap(t => doc.splitTextToSize(t.replace(/\s+/g, ' ').trim(), textBreite) as string[]);

        if (nameGekuerzt || hoeheVon(name.length, erlaeuterung.length) > innen) {
            // Der Hinweis bekommt seinen Platz ZUERST — er kann selbst umbrechen.
            const hinweis = doc.splitTextToSize(GEKUERZT, textBreite) as string[];
            const fuerHinweis = 0.8 + hinweis.length * zhErl;
            // Dann der Name (mindestens eine Zeile), dann die Erlaeuterung —
            // jedes Budget nach unten bei 0 abgeschnitten, nie negativ.
            const maxName = Math.max(1, Math.floor(Math.max(0, innen - fuerHinweis) / zhName));
            if (name.length > maxName) {
                name = name.slice(0, maxName);
                name[maxName - 1] = mitAuslassung(name[maxName - 1], b.fachpraxis ? ' *' : '');
            }
            const maxErl = Math.max(0, Math.floor(Math.max(0, innen - name.length * zhName - fuerHinweis) / zhErl));
            if (erlaeuterung.length > maxErl) {
                erlaeuterung = erlaeuterung.slice(0, maxErl);
                if (maxErl > 0) erlaeuterung[maxErl - 1] = mitAuslassung(erlaeuterung[maxErl - 1]);
            }
            erlaeuterung = [...erlaeuterung, ...hinweis];
        }
        const hoehe = 2 * POLSTER + hoeheVon(name.length, erlaeuterung.length);
        const s = { name, erlaeuterung, hoehe };
        satz.set(b.id, s);
        return s;
    };

    const zeichneUnterlage = (b: HandbuchBlock, x: number, yZelle: number) => {
        const s = satzVon(b);
        let ty = yZelle + POLSTER + SCHRIFT.name * PT * 0.8;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.name);
        farbe(FARBE.text);
        s.name.forEach(t => { doc.text(t, x + POLSTER, ty); ty += zeilenhoehe(SCHRIFT.name); });
        if (!s.erlaeuterung.length) return;
        ty += 0.8 - zeilenhoehe(SCHRIFT.name) + zeilenhoehe(SCHRIFT.erlaeuterung);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.erlaeuterung);
        farbe(FARBE.grau);
        s.erlaeuterung.forEach(t => { doc.text(t, x + POLSTER, ty); ty += zeilenhoehe(SCHRIFT.erlaeuterung); });
    };

    const optionenFuer = (gruppe: HandbuchModell['gruppen'][number], startY: number): UserOptions => ({
        startY,
        margin: rand,
        theme: 'grid',
        // Kopfzeilen auf jeder Folgeseite wiederholen (Fortsetzungskopf),
        // Positionen nie ueber eine Seite reissen.
        showHead: 'everyPage',
        rowPageBreak: 'avoid',
        willDrawPage: lh.willDrawPage,
        styles: grundstil,
        headStyles: kopfstil,
        head: [
            [{
                content: `${gruppe.nummer}   ${gruppe.titel}`,
                colSpan: 6,
                styles: { fillColor: FARBE.gruppe, fontSize: SCHRIFT.gruppe, fontStyle: 'bold' },
            }],
            ['Pos.', 'Unterlage / Erläuterung', 'Art', 'Lief.', 'Status', 'Notizen'],
        ],
        body: gruppe.bloecke.map((b): RowInput => [
            b.pos,
            // Leer: Name und Erlaeuterung zeichnet `didDrawCell` in zwei
            // Schriftgroessen; die Zelle haelt dafuer die Hoehe frei.
            { content: '', styles: { minCellHeight: satzVon(b).hoehe } },
            { content: b.art, styles: { halign: 'center' } },
            b.lieferant,
            b.status.replace(' ', '\n'),
            { content: '', styles: { minCellHeight: NOTIZ_HOEHE } },
        ]),
        columnStyles: {
            0: { cellWidth: sp.pos },
            1: { cellWidth: sp.unterlage },
            2: { cellWidth: sp.art },
            3: { cellWidth: sp.lief },
            4: { cellWidth: sp.status },
            5: { cellWidth: sp.notizen },
        },
        didDrawCell: data => {
            if (data.section !== 'body') return;
            const b = gruppe.bloecke[data.row.index];
            if (!b) return;
            if (data.column.index === 1) zeichneUnterlage(b, data.cell.x, data.cell.y);
            if (data.column.index === 5) {
                felder.push({
                    feld: b.feldName,
                    seite: aktuelleSeite(doc),
                    x: data.cell.x,
                    yOben: data.cell.y,
                    breite: data.cell.width,
                    hoehe: data.cell.height,
                    text: b.notiz,
                });
            }
        },
    });

    if (!modell.gruppen.length) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.tabelle);
        farbe(FARBE.text);
        doc.text('In dieser Ansicht steht keine Unterlage.', margin, y + 3);
    }

    modell.gruppen.forEach(gruppe => {
        // Erst messen: Gruppen- und Spaltenkopf duerfen nicht allein am
        // Seitenende stehen — autoTable prueft beim Start nur den Kopf.
        let tabelle = __createTable(doc, optionenFuer(gruppe, y));
        const erste = tabelle.body[0] ? tabelle.body[0].height : 0;
        if (y + tabelle.getHeadHeight(tabelle.columns) + erste > unterkante) {
            lh.addPage();
            y = rand.top;
            tabelle = __createTable(doc, optionenFuer(gruppe, y));
        }
        __drawTable(doc, tabelle);
        y = endeVon(tabelle, y) + 5;
    });

    // Fusszeile und Seitenzahl zuletzt — vorher steht die Gesamtzahl nicht fest.
    drawFooters(doc, company, lh, geo);
    zeichneDokumentzeile(doc, modell, company, lh.hasStationery, lh.botMargin);
    return felder;
};

/**
 * Links unten auf jeder Seite: welche Unterlage das ist — auf der Hoehe der
 * Seitenzahl, die `drawFooters` rechts setzt (dieselben drei Faelle).
 */
const zeichneDokumentzeile = (
    doc: jsPDF,
    modell: HandbuchModell,
    company: CompanyInfo,
    briefpapier: boolean,
    unterrand: number,
) => {
    const { margin, pageHeight } = A4_GEOMETRY;
    const spaltenFuss = (company.footerColumns || []).some(c => (c || []).some(l => !!(l && l.trim())));
    const yZeile = briefpapier
        ? pageHeight - Math.max(unterrand - 5, 4)
        : spaltenFuss ? pageHeight - 23 : pageHeight - 20;
    const projekt = modell.kopf.projektNummer ? `Projekt-Nr. ${modell.kopf.projektNummer}` : modell.kopf.bauvorhaben;
    const text = ['Unterlagenliste', projekt, `Stand ${modell.kopf.stand}`].filter(Boolean).join(' · ');
    for (let i = 1; i <= doc.getNumberOfPages(); i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.fusszeile);
        doc.setTextColor(FARBE.grau[0], FARBE.grau[1], FARBE.grau[2]);
        doc.text(text, margin, yZeile);
    }
};

// ─── Felder mit pdf-lib ─────────────────────────────────────────────────────

/**
 * Hoechstens so viele Zeichen gehen ins Erscheinungsbild. Sichtbar sind
 * ohnehin nur rund vier Zeilen; der Zeilenumbruch von pdf-lib wird bei
 * langen Texten aber sehr langsam (Sekunden je Feld). Der WERT bleibt
 * vollstaendig, und `NeedAppearances` laesst die Programme das Bild aus
 * dem ganzen Wert neu zeichnen.
 */
const BILD_ZEICHEN = 800;

/**
 * Setzt den Wert eines Felds und sein Erscheinungsbild.
 *
 * Das Bild entsteht aus einer Fassung, in der Zeichen ausserhalb von
 * WinAnsi durch „?" ersetzt und lange Texte gekuerzt sind — pdf-lib braeche
 * sonst ab bzw. braeuchte zu lange. Danach wird der WERT auf den
 * Originaltext zurueckgesetzt; nur er wird eingelesen.
 */
const setzeFeldtext = (feld: PDFTextField, text: string, font: PDFFont, zeichen: ReadonlySet<number>) => {
    const wert = String(text || '').replace(/\r\n?/g, '\n');
    let bild = '';
    for (const ch of wert.replace(/\t/g, ' ')) {
        if (bild.length >= BILD_ZEICHEN) { bild += ' …'; break; }
        const cp = ch.codePointAt(0) as number;
        bild += ch === '\n' || zeichen.has(cp) ? ch : '?';
    }
    feld.setText(bild || undefined);
    feld.updateAppearances(font);
    if (bild !== wert) feld.acroField.setValue(PDFHexString.fromText(wert));
};

export interface HandbuchPdfOptionen {
    /** Nur fuer Tests: eigenes jsPDF-Objekt. */
    __docFactory?: () => jsPDF;
}

/** Baut das Handbuch: Layout mit jsPDF, Felder mit pdf-lib. */
export const erzeugeHandbuchPdf = async (
    modell: HandbuchModell,
    company: CompanyInfo,
    opt: HandbuchPdfOptionen = {},
): Promise<Uint8Array> => {
    const doc = opt.__docFactory ? opt.__docFactory() : new jsPDF({ format: 'a4', unit: 'mm', compress: true });
    const felder = zeichneHandbuch(doc, modell, company || {});

    // updateMetadata: false — sonst traegt pdf-lib sich als Ersteller/Produzent ein
    // und ueberschreibt die Angaben von jsPDF; das Blatt soll aussehen wie jedes
    // andere PDF der App.
    const pdf = await PDFDocument.load(doc.output('arraybuffer'), { updateMetadata: false });
    const form = pdf.getForm();
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    const zeichen = new Set(font.getCharacterSet());
    const seiten = pdf.getPages();
    const ohneRahmen = { backgroundColor: undefined, borderColor: undefined, borderWidth: 0 };

    felder.forEach(f => {
        const seite = seiten[f.seite - 1];
        const feld = form.createTextField(f.feld);
        feld.enableMultiline();
        feld.addToPage(seite, {
            ...ohneRahmen,
            x: f.x * MM + 1,
            y: seite.getHeight() - (f.yOben + f.hoehe) * MM + 1,
            width: f.breite * MM - 2,
            height: f.hoehe * MM - 2,
            font,
            textColor: rgb(0.06, 0.09, 0.16),
        });
        // Erst nach `addToPage`: die Schriftgroesse steht im DA-Eintrag, den
        // pdf-lib dort anlegt. Feste Groesse statt „auto", damit vier Zeilen
        // sichtbar sind und laengerer Text scrollt statt zu schrumpfen.
        feld.setFontSize(SCHRIFT.feld);
        setzeFeldtext(feld, f.text, font, zeichen);
    });

    // Metadaten: unsichtbar, schreibgeschuetzt, auf Seite 1.
    const versteckt = (name: string, wert: string) => {
        const feld = form.createTextField(name);
        feld.addToPage(seiten[0], { ...ohneRahmen, x: 0, y: 0, width: 0, height: 0, hidden: true, font });
        setzeFeldtext(feld, wert, font, zeichen);
        feld.enableReadOnly();
    };
    versteckt(FELD_META, JSON.stringify(modell.meta));
    versteckt(FELD_AUSGANG, JSON.stringify(modell.ausgang));

    // Edge, Chrome und Acrobat sollen die Felder aus dem Wert neu zeichnen.
    form.acroForm.dict.set(PDFName.of('NeedAppearances'), PDFBool.True);
    pdf.setTitle(modell.titel);

    return pdf.save({ updateFieldAppearances: false });
};

/** Anschluss fuer die Oberflaeche — wirft nie (Muster `planungIntegration.ts`). */
export const baueHandbuchPdf = async (modell: HandbuchModell, kontext: PdfKontext): Promise<Anschluss<Uint8Array>> => {
    try {
        const company = buildCompanyForPdf(
            kontext.company as never,
            kontext.quoteConfig as never,
            kontext.invoiceConfig as never,
        );
        return { status: 'ok', wert: await erzeugeHandbuchPdf(modell, company) };
    } catch (e) {
        return { status: 'fehler', fehler: `Das Handbuch konnte nicht erzeugt werden: ${e instanceof Error ? e.message : String(e)}` };
    }
};

/** Bytes als Datei herunterladen (Browser). */
export const speichereHandbuch = (bytes: Uint8Array, dateiname: string): Anschluss<true> => {
    try {
        const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        try {
            const a = document.createElement('a');
            a.href = url;
            a.download = dateiname;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } finally {
            // Verzoegert freigeben: manche Browser lesen den Link erst nach dem Klick.
            window.setTimeout(() => URL.revokeObjectURL(url), 10000);
        }
        return { status: 'ok', wert: true };
    } catch (e) {
        return { status: 'fehler', fehler: `Das Handbuch konnte nicht gespeichert werden: ${e instanceof Error ? e.message : String(e)}` };
    }
};

// ─── Einlesen ───────────────────────────────────────────────────────────────

/**
 * Liest Notizen und Metadaten aus einem (ausgefuellten) Handbuch.
 *
 * Wirft mit einer verstaendlichen Meldung, wenn die Datei zu gross oder kein
 * lesbares PDF ist oder ihre Felder nicht so aussehen, wie das Handbuch sie
 * anlegt (`werteHandbuchFelderAus`: Dubletten, falsche Feldtypen, Grenzen).
 * Ob es zum Projekt passt, entscheidet `pruefeHandbuch`.
 */
export const liesHandbuchPdf = async (bytes: Uint8Array | ArrayBuffer): Promise<HandbuchGelesen> => {
    // Zweite Reihe hinter der Pruefung von `File.size` in der Oberflaeche:
    // pdf-lib laeuft ohne Worker im Hauptfaden.
    const groesse = pruefeHandbuchDatei(bytes.byteLength);
    if (groesse) throw new Error(groesse);
    let pdf: PDFDocument;
    try {
        pdf = await PDFDocument.load(bytes, { updateMetadata: false });
    } catch (e) {
        const text = e instanceof Error ? e.message : String(e);
        throw new Error(/encrypt/i.test(text)
            ? 'Das PDF ist verschlüsselt und lässt sich nicht einlesen. Bitte ohne Kennwortschutz speichern.'
            : 'Die Datei ist kein lesbares PDF.');
    }
    const unlesbar = 'Die Formularfelder des PDF sind nicht lesbar. Bitte ein neu erzeugtes Handbuch verwenden.';
    let felder: PDFField[];
    try {
        felder = pdf.getForm().getFields();
    } catch {
        throw new Error(unlesbar);
    }
    // 1. Erst die Zahl — bevor auch nur ein Name gelesen wird.
    const zuViele = pruefeFeldanzahl(felder.length);
    if (zuViele) throw new Error(zuViele);

    // 2. Namen aller Felder, WERTE nur von Notiz- und Metadatenfeldern: der
    //    Wert eines fremden Felds wird nie gelesen. Jede Grenze greift beim
    //    Lesen; nach dem ersten Verstoss wird nichts weiter gelesen.
    const roh: RohFeld[] = [];
    let notizen = 0;
    for (const feld of felder) {
        let name: string;
        try {
            name = feld.getName();
        } catch {
            throw new Error(unlesbar);
        }
        if (!istHandbuchFeld(name) || !(feld instanceof PDFTextField)) {
            // Fremdes Feld: nur gezaehlt. Ein Notiz- oder Metadatenfeld, das
            // kein Textfeld ist, weist `werteHandbuchFelderAus` zurueck.
            roh.push({ name, text: null });
            continue;
        }
        if (name !== FELD_META && name !== FELD_AUSGANG && ++notizen > HANDBUCH_GRENZEN.notizen) {
            throw new Error(`Das PDF hat mehr als ${HANDBUCH_GRENZEN.notizen} Notizfelder. So viele Unterlagen kennt die Checkliste nicht. Bitte ein neu erzeugtes Handbuch verwenden.`);
        }
        let text: string;
        try {
            text = feld.getText() || '';
        } catch {
            throw new Error(unlesbar);
        }
        const verstoss = pruefeFeldwert(name, text);
        if (verstoss) throw new Error(verstoss);
        roh.push({ name, text });
    }

    // 3. Gestalt: Dubletten, Feldtypen, beschaedigte Namen, Metadaten.
    const ergebnis = werteHandbuchFelderAus(roh);
    if (ergebnis.status === 'fehler') throw new Error(ergebnis.fehler);
    return ergebnis.wert;
};
